// "Try it now", in the homepage's section for people who aren't developers
// (Home.tsx's #humans): for someone with nothing built yet, an idea (one of
// a few, or their own), the prompt it makes, and a button per AI that opens
// a new chat with that prompt already typed in, through each one's own ?q=
// link (the same ones whatislocalhost.com's launchpad uses). A click copies
// the prompt too, for a chat that opens empty. The links are real hrefs from
// the first render, so they work before the page hydrates.

import { useState } from 'react';
import { AI_LINKS, CHATGPT_PLUGIN_URL, TRY_PROMPT } from '../content/quickstart';
import { AiMark, type AiId } from './AiMark';

// Tools a business pays a subscription for, so what it builds reads as real
// software rather than a toy. Three, so they fit on one row.
const DEFAULT_IDEA = 'a project tracker for my team';

const IDEAS = [
  { label: 'Support desk', idea: 'a support desk where customers open tickets and my team replies' },
  { label: 'Bookings', idea: 'a booking page where my clients pick a time slot' },
  { label: 'Waitlist', idea: 'a launch waitlist where referrals move people up the line' },
];

// Claude Code's is a claude-cli:// link: it opens a terminal on this
// computer, in the home folder, rather than a tab, so it gets no target.
const AIS: { id: AiId; name: string; hint: string; href: (p: string) => string; local?: boolean }[] = [
  { id: 'chatgpt', name: 'ChatGPT', hint: 'Open with prompt', href: AI_LINKS.chatgpt },
  { id: 'claude', name: 'Claude', hint: 'Open with prompt', href: AI_LINKS.claude },
  { id: 'grok', name: 'Grok', hint: 'Open with prompt', href: AI_LINKS.grok },
  { id: 'claude-code', name: 'Claude Code', hint: 'Opens your terminal', href: AI_LINKS.claudeCode, local: true },
];

const [BEFORE, AFTER] = TRY_PROMPT.split('{idea}');

export function TryInYourAI() {
  const [idea, setIdea] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const what = idea.trim() || DEFAULT_IDEA;
  const prompt = BEFORE + what + AFTER;

  const copy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
    } catch {
      return;   // Clipboard access can be blocked; the chat still opens with the prompt.
    }
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 2400);
  };

  return (
    <div className="try-card">
      <h3>Nothing built yet? Try it now</h3>
      <p>Pick one or type your own: your AI builds it, Cloudish runs it.</p>
      <label className="sr-only" htmlFor="try-idea">
        What should your app be?
      </label>
      <input
        id="try-idea"
        type="text"
        autoComplete="off"
        placeholder={DEFAULT_IDEA}
        value={idea}
        onChange={(e) => setIdea(e.target.value)}
      />
      <div className="try-ideas">
        {IDEAS.map((i) => (
          <button key={i.label} type="button" className="try-idea" aria-pressed={idea === i.idea} onClick={() => setIdea(i.idea)}>
            {i.label}
          </button>
        ))}
      </div>
      <p className="try-prompt">
        {BEFORE}
        <mark>{what}</mark>
        {AFTER}
      </p>
      <div className="ai-buttons">
        {AIS.map((ai) => (
          <a
            key={ai.id}
            className={`ai-btn ai-btn-${ai.id}`}
            href={ai.href(prompt)}
            {...(ai.local ? {} : { target: '_blank', rel: 'noopener' })}
            onClick={() => copy(ai.id)}
          >
            <span className="ai-btn-mark">
              <AiMark id={ai.id} />
            </span>
            <span>
              {ai.name}
              <small>{copied === ai.id ? 'Prompt copied too' : ai.hint}</small>
            </span>
          </a>
        ))}
      </div>
      <p className="try-note">
        In ChatGPT, add the{' '}
        <a href={CHATGPT_PLUGIN_URL} target="_blank" rel="noopener">
          Cloudish plugin
        </a>{' '}
        first. The prompt is copied too.
      </p>
    </div>
  );
}
